import { useState, useEffect } from "react"

import { useMutationPostCreateJobs } from "api/reactQuery/mutations/jobs"
import { ISuggestedCreateJobs } from "api/reactQuery/mutations/jobs.types"
import { useDialog } from "hooks/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import {
  elementSuggestedJobs,
  msSuggestedJobs,
  psSuggestedJobs,
} from "pages/jobs/addSuggestedJobs/suggestedJobs"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useParams } from "react-router"
import { getErrorMessageFromTab } from "utils/api"

export const useSuggestedJobs = () => {
  const params = useParams()
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const queryClient = useQueryClient()

  const [checkedSuggestedJobs, setCheckedSuggestedJobs] = useState<
    ISuggestedCreateJobs[]
  >([])
  const [errorSuggested, setErrorSuggested] = useState("")

  const {
    isOpen: isDialogOpenSuggested,
    handleOpen: handleOpenDialogSuggested,
    handleClose: handleCloseDialogSuggested,
  } = useDialog()

  const {
    isOpen: isDialogCloseSuggestedJobs,
    handleOpen: handleOpenDialogCloseSuggestedJobs,
    handleClose: handleCloseDialogCloseSuggestedJobs,
  } = useDialog()

  const { mutate: createSuggestedJobs } = useMutationPostCreateJobs({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs")
        showSnackbar({
          title: t("jobs.succeedCreateJob"),
          variant: "success",
        })
        setCheckedSuggestedJobs([])
        handleCloseDialogSuggested()
      },
      onError: (error) => {
        showSnackbar({
          title: getErrorMessageFromTab(error),
          variant: "error",
        })
      },
    },
  })

  const handleSuggestedJobs = (elements: ISuggestedCreateJobs) => {
    const currentPosition = checkedSuggestedJobs.findIndex(
      (el) => el.id === elements.id
    )
    const newChecked = [...checkedSuggestedJobs]

    if (currentPosition === -1) {
      newChecked.push(elements)
      elements.check = true
    } else {
      newChecked.splice(currentPosition, 1)
      elements.check = false
    }
    setCheckedSuggestedJobs(newChecked)
  }

  const handleCreateSuggestedJobs = () => {
    if (checkedSuggestedJobs.length === 0) {
      setErrorSuggested(t("jobs.atleastOneCheckbox"))
      return
    }

    if (
      !!checkedSuggestedJobs.find(
        (el) => (el.title && el.title.length <= 3) || !el.title
      )
    ) {
      showSnackbar({
        title: t("jobs.selectedItemsShouldHaveTitleOfAtLeast3Characters"),
        variant: "error",
      })
      return
    }

    if (!!checkedSuggestedJobs.find((el) => el.salary === 0)) {
      showSnackbar({
        title: t("jobs.amountOfSelectedItemsHaveToBePositiveNumber"),
        variant: "error",
      })
      return
    }

    createSuggestedJobs({
      classId: Number(params.classId),
      data: checkedSuggestedJobs,
    })
  }

  const handleCloseWithConfirmation = () => {
    setCheckedSuggestedJobs([])
    setErrorSuggested("")
    elementSuggestedJobs.forEach((el) => (el.check = false))
    msSuggestedJobs.forEach((el) => (el.check = false))
    psSuggestedJobs.forEach((el) => (el.check = false))
    handleCloseDialogSuggested()
    handleCloseDialogCloseSuggestedJobs()
  }

  useEffect(() => {
    if (checkedSuggestedJobs.length > 0) {
      setErrorSuggested("")
    }
  }, [checkedSuggestedJobs])

  return {
    checkedSuggestedJobs,
    setCheckedSuggestedJobs,
    errorSuggested,
    isDialogOpenSuggested,
    handleOpenDialogSuggested,
    isDialogCloseSuggestedJobs,
    handleOpenDialogCloseSuggestedJobs,
    handleCloseDialogCloseSuggestedJobs,
    handleSuggestedJobs,
    handleCreateSuggestedJobs,
    handleCloseWithConfirmation,
  }
}
